import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Sparkles, Droplets, Flower2, ArrowRight, BadgeCheck, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, FadeUp, ScaleIn } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { HERO_PRODUCT, PRODUCTS, formatPrice } from "@/data/products";
import { useCart } from "@/context/CartContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const CHAPTERS = [
  {
    num: "01", icon: Droplets, title: "Infiltration Cellulaire",
    text: "Quatre poids d'acide hyaluronique pénètrent chaque couche de l'épiderme pour une hydratation profonde qui dure 24 heures. La peau boit, littéralement.",
    image: HERO_PRODUCT.images[2],
  },
  {
    num: "02", icon: Sparkles, title: "Synapses de Collagène",
    text: "Le bio-collagène hydrolysé, ultra-pénétrant, aide à restaurer l'élasticité et le rebondi. Les ridules de déshydratation s'estompent, la peau se repulpe.",
    image: HERO_PRODUCT.serumImage,
  },
  {
    num: "03", icon: Flower2, title: "Éclat Rosé Intemporel",
    text: "Les probiotiques rééquilibrent la flore cutanée et révèlent ce fini « glass skin » lumineux et frais, signature du rituel Relax.",
    image: HERO_PRODUCT.textureImage,
  },
];

const TESTIMONIALS = [
  { name: "Camille R.", city: "Paris", text: "Ma peau n'a jamais été aussi rebondie. Le gel est frais, le fini glowy… c'est devenu mon rituel du dimanche soir.", rating: 5 },
  { name: "Inès M.", city: "Lyon", text: "Résultat visible dès la première pose. Teint lumineux, peau ultra douce. Le rapport qualité-prix est incroyable.", rating: 5 },
  { name: "Sophie L.", city: "Bordeaux", text: "Sensible de nature, ma peau l'adore : aucune réaction, juste du confort et de l'éclat. Je recommande les yeux fermés.", rating: 4.5 },
];

const FAQ_ITEMS = [
  { q: "À quelle fréquence utiliser le masque ?", a: "Nous recommandons 2 à 3 poses par semaine pour un résultat optimal, ou en cure intensive quotidienne pendant 5 jours avant un événement. Chaque pose dure 20 à 30 minutes." },
  { q: "Convient-il à tous les types de peau ?", a: "Oui. La formule gel au bio-collagène est testée sous contrôle dermatologique et convient à toutes les peaux, y compris sensibles. Elle est particulièrement recommandée pour les peaux déshydratées, ternes ou en manque de confort." },
  { q: "Quels sont les délais et frais de livraison ?", a: "Expédition sous 24h. Colissimo Standard 2-3 jours ouvrés à 3,90 €, Chronopost Express 24h à 7,90 €. La livraison est offerte dès 25 € d'achat." },
  { q: "Puis-je être remboursée si le produit ne me convient pas ?", a: "Absolument. Vous disposez de 30 jours après réception pour nous retourner le produit non utilisé et être intégralement remboursée. Voir notre page Livraison & Retours." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const { addItem, setDrawerOpen } = useCart();
  const [selectedVariant, setSelectedVariant] = useState(HERO_PRODUCT.variants[1].id);

  const handleAdd = () => {
    addItem(selectedVariant, 1);
    setDrawerOpen(true);
    toast.success("Ajouté à votre panier ✦");
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden grain-overlay bg-cream pt-24 pb-16">
        <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-blush blur-3xl opacity-70 pointer-events-none" />
        <div className="absolute bottom-0 -left-40 w-[28rem] h-[28rem] rounded-full bg-rose-gold/20 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative z-10 w-full">
          <motion.div style={{ y: heroTextY }}>
            <motion.p
              data-testid="hero-badge"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-6 flex items-center gap-2"
            >
              <Sparkles size={14} /> Offre du moment · -16 % sur le Pack Rituel
            </motion.p>
            <h1 data-testid="hero-title" className="font-serif font-light tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-onyx">
              <MaskedLines
                lines={["L'éclat", "bio-collagène", "à l'état pur."]}
                lineClassName=""
              />
            </h1>
            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg font-light text-muted-warm max-w-md leading-relaxed"
            >
              {HERO_PRODUCT.tagline} Le rituel girly & chic que votre peau attendait.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-6 flex items-center gap-3"
            >
              <div className="flex items-center gap-0.5" data-testid="hero-rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-rose-gold text-rose-gold" />
                ))}
              </div>
              <p className="text-sm text-muted-warm">
                <span className="font-semibold text-onyx">{HERO_PRODUCT.rating}/5</span> — {HERO_PRODUCT.reviewsCount.toLocaleString("fr-FR")} avis vérifiés
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-5"
            >
              <Link
                to="/produit/pack-rituel"
                data-testid="hero-cta-button"
                className="group rounded-full bg-terracotta text-cream pl-8 pr-3 py-3 text-sm font-semibold tracking-wide hover:bg-onyx transition-colors flex items-center gap-4"
              >
                Découvrir le Pack Rituel
                <span className="w-9 h-9 rounded-full bg-rose-gold text-onyx flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <ArrowRight size={16} />
                </span>
              </Link>
              <div data-testid="hero-price-badge" className="flex flex-col">
                <span className="font-serif text-3xl text-terracotta">
                  <span className="text-lg text-stone-400 line-through mr-2">47,60 €</span>39,90 €
                </span>
                <span className="text-[11px] font-mono uppercase tracking-widest text-muted-warm">4 produits · livraison offerte</span>
                <Link to="/produit/masque-bio-collagene" data-testid="hero-single-link" className="text-[11px] text-terracotta underline underline-offset-2 mt-1.5 hover:text-onyx transition-colors">
                  ou le masque seul dès 10,90 €
                </Link>
              </div>
            </motion.div>
          </motion.div>
          <div className="relative flex justify-center lg:justify-end">
            <motion.div
              style={{ y: heroImgY }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="arch-frame overflow-hidden w-72 sm:w-96 h-[26rem] sm:h-[34rem] shadow-2xl shadow-rose-gold/30">
                <img src="/images/range-full.jpg" alt="Pack Rituel Complet Relax — 3 masques gel et masque de nuit" className="w-full h-full object-cover scale-105" />
              </div>
              <motion.div
                className="absolute -bottom-6 -left-6 sm:-left-12 bg-white rounded-2xl shadow-xl shadow-rose-gold/20 px-5 py-4 border border-blush-deep animate-float-soft"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.7 }}
              >
                <p className="font-mono text-[10px] tracking-widest uppercase text-muted-warm">Rituel complet</p>
                <p className="font-serif text-lg text-onyx">3 masques + soin de nuit</p>
              </motion.div>
              <motion.div
                className="absolute -top-4 -right-2 sm:-right-8 bg-terracotta text-cream rounded-full px-5 py-3 shadow-lg rotate-6"
                initial={{ opacity: 0, rotate: -10, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 6, scale: 1 }}
                transition={{ delay: 1.35, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-serif italic text-sm">PACK RITUEL · -16 %</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section id="rituel" data-testid="manifesto-section" className="py-24 sm:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4">Le Manifeste Relax</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx max-w-2xl leading-tight">
              Trois chapitres pour une peau qui rayonne.
            </h2>
          </FadeUp>
          <div className="mt-16 space-y-20 sm:space-y-28">
            {CHAPTERS.map((ch, i) => (
              <div key={ch.num} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
                <FadeUp delay={0.1}>
                  <div className="flex items-start gap-6">
                    <span data-testid={`manifesto-chapter-number-${ch.num}`} className="font-mono text-6xl sm:text-8xl text-rose-gold/50 leading-none select-none">{ch.num}</span>
                    <div className="pt-2 sm:pt-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-10 h-10 rounded-full bg-blush flex items-center justify-center"><ch.icon size={18} className="text-terracotta" /></span>
                        <h3 className="font-serif text-2xl sm:text-3xl text-onyx">{ch.title}</h3>
                      </div>
                      <p className="text-sm sm:text-base text-muted-warm leading-relaxed max-w-md">{ch.text}</p>
                    </div>
                  </div>
                </FadeUp>
                <ScaleIn delay={0.15}>
                  <div className={`overflow-hidden shadow-xl shadow-rose-gold/20 ${i % 2 === 0 ? "rounded-[2rem] rounded-tr-[6rem]" : "rounded-[2rem] rounded-tl-[6rem]"}`}>
                    <img src={ch.image} alt={ch.title} className="w-full h-72 sm:h-96 object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                </ScaleIn>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO RITUAL */}
      <section data-testid="video-section" className="py-24 sm:py-32 bg-onyx relative overflow-hidden">
        <div className="absolute -top-24 left-1/3 w-[30rem] h-[30rem] rounded-full bg-rose-gold/15 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10">
          <FadeUp>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-rose-gold mb-5">Le Rituel en Vidéo</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#FAF7F2] leading-tight">
              Regardez le gel fondre, la peau boire.
            </h2>
            <p className="mt-6 text-sm sm:text-base font-light text-stone-400 leading-relaxed max-w-md">
              Une texture gel seconde peau qui épouse chaque contour du visage. Posez, respirez, laissez le bio-collagène et l'acide hyaluronique faire leur travail — votre seul effort sera de vous accorder ce moment.
            </p>
            <div className="mt-9 grid grid-cols-3 gap-4 max-w-md">
              {[
                { value: "10 s", label: "d'application" },
                { value: "20-30 min", label: "de pose relaxante" },
                { value: "24 h", label: "d'hydratation" },
              ].map((s, i) => (
                <div key={i} data-testid={`video-stat-${i}`} className="border-t border-rose-gold/30 pt-4">
                  <p className="font-serif text-2xl sm:text-3xl text-[#FAF7F2]">{s.value}</p>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </FadeUp>
          <ScaleIn delay={0.15} className="flex justify-center lg:justify-end">
            <div className="relative">
              <div className="arch-frame overflow-hidden w-72 sm:w-80 h-[26rem] sm:h-[30rem] shadow-2xl shadow-rose-gold/20 border border-white/10">
                <video
                  data-testid="ritual-video"
                  src="https://videos.pexels.com/video-files/5937385/5937385-hd_1080_1920_24fps.mp4"
                  poster={HERO_PRODUCT.images[2]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover"
                />
              </div>
              <motion.div
                className="absolute -bottom-5 -left-8 bg-[#FAF7F2] rounded-2xl shadow-xl px-5 py-4 animate-float-soft"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <p className="font-mono text-[10px] tracking-widest uppercase text-muted-warm">Texture gel</p>
                <p className="font-serif italic text-lg text-onyx">seconde peau ✦</p>
              </motion.div>
            </div>
          </ScaleIn>
        </div>
      </section>

      {/* PRODUCT SPOTLIGHT */}
      <section data-testid="spotlight-section" className="py-24 sm:py-32 bg-blush relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-rose-gold/20 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <ScaleIn>
            <div className="relative">
              <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-terracotta/20">
                <img src={HERO_PRODUCT.images[1]} alt={HERO_PRODUCT.name} className="w-full h-[26rem] sm:h-[32rem] object-cover" />
              </div>
              <div className="absolute top-6 left-6 bg-onyx/85 backdrop-blur text-cream rounded-full px-5 py-2.5">
                <p className="font-mono text-xs tracking-widest uppercase">{HERO_PRODUCT.badge}</p>
              </div>
            </div>
          </ScaleIn>
          <FadeUp delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4">La Star de Votre Salle de Bain</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx leading-tight">{HERO_PRODUCT.name}</h2>
            <p className="mt-5 text-sm sm:text-base text-muted-warm leading-relaxed max-w-lg">
              Enrichi en bio-collagène, acide hyaluronique et actifs hydratants, ce masque gel épouse parfaitement les contours du visage pour un soin confortable, frais et intensément repulpant.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {HERO_PRODUCT.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-onyx">
                  <BadgeCheck size={16} className="text-terracotta mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="spotlight-variant-selector">
              {HERO_PRODUCT.variants.map((v) => (
                <button
                  key={v.id}
                  data-testid={`spotlight-variant-${v.id}`}
                  onClick={() => setSelectedVariant(v.id)}
                  className={`rounded-2xl border px-5 py-3.5 text-left transition-all ${
                    selectedVariant === v.id
                      ? "border-terracotta bg-white shadow-md shadow-rose-gold/20"
                      : "border-blush-deep bg-white/50 hover:border-rose-gold"
                  }`}
                >
                  <p className="text-sm font-semibold text-onyx flex items-center gap-2">
                    {v.label}
                    {v.popular && <span className="text-[10px] font-mono uppercase bg-rose-gold/20 text-terracotta rounded-full px-2 py-0.5">Populaire</span>}
                  </p>
                  <p className="text-xs text-muted-warm mt-0.5">{v.sublabel} · {formatPrice(v.price)}</p>
                </button>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid="spotlight-add-to-cart-button"
                onClick={handleAdd}
                className="rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-semibold tracking-wide hover:bg-onyx transition-colors flex items-center gap-2"
              >
                Ajouter au panier — {formatPrice(HERO_PRODUCT.variants.find((v) => v.id === selectedVariant).price)}
              </button>
              <Link to="/produit" data-testid="spotlight-details-link" className="text-sm font-medium text-terracotta underline underline-offset-4 hover:text-onyx transition-colors">
                Voir la fiche complète
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-warm">
              <span className="flex items-center gap-1.5"><Truck size={14} className="text-rose-gold" /> Expédié sous 24h</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-rose-gold" /> Paiement 100% sécurisé</span>
              <span className="flex items-center gap-1.5"><RotateCcw size={14} className="text-rose-gold" /> Satisfait ou remboursé 30 j</span>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" data-testid="collection-section" className="py-24 sm:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4">La Collection</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx max-w-2xl leading-tight">
              Un rituel complet, quatre complices.
            </h2>
          </FadeUp>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.1}>
                <Link
                  to={`/produit/${p.slug}`}
                  data-testid={`collection-card-${p.slug}`}
                  className="group block bg-white rounded-3xl border border-blush-deep overflow-hidden hover:shadow-xl hover:shadow-rose-gold/15 hover:-translate-y-1.5 transition-all duration-500"
                >
                  <div className="relative overflow-hidden h-64">
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 bg-onyx/80 backdrop-blur text-cream text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1.5">{p.badge}</span>
                  </div>
                  <div className="p-6">
                    <p className="font-serif text-xl text-onyx leading-snug">{p.shortName}</p>
                    <p className="text-xs text-muted-warm mt-2 leading-relaxed line-clamp-2">{p.tagline}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm font-semibold text-terracotta">dès {formatPrice(p.variants[0].price)}</p>
                      <span className="w-8 h-8 rounded-full bg-blush flex items-center justify-center text-terracotta group-hover:bg-terracotta group-hover:text-cream transition-colors">
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="avis" data-testid="testimonials-section" className="py-24 sm:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp className="text-center">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4">Elles en Parlent Mieux que Nous</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx">1 240 peaux conquises.</h2>
          </FadeUp>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={t.name} delay={i * 0.12}>
                <div data-testid={`testimonial-card-${i}`} className="bg-white rounded-3xl border border-blush-deep p-8 h-full flex flex-col hover:shadow-xl hover:shadow-rose-gold/10 hover:-translate-y-1 transition-all duration-500">
                  <div className="flex items-center gap-1 mb-5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={15} className={s < Math.floor(t.rating) ? "fill-rose-gold text-rose-gold" : "fill-blush text-blush"} />
                    ))}
                  </div>
                  <p className="font-serif italic text-lg text-onyx leading-relaxed flex-1">« {t.text} »</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center font-serif text-terracotta text-lg">{t.name[0]}</div>
                    <div>
                      <p className="text-sm font-semibold text-onyx">{t.name}</p>
                      <p className="text-xs text-muted-warm flex items-center gap-1"><BadgeCheck size={12} className="text-[#2E6B4F]" /> Achat vérifié · {t.city}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-24 sm:py-32 bg-blush">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp className="text-center">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4">On Répond à Tout</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx">Vos questions, nos réponses.</h2>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-12">
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {FAQ_ITEMS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-blush-deep px-6 overflow-hidden">
                  <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-medium text-onyx hover:text-terracotta py-5 text-sm sm:text-base">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-warm leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-14 text-center">
            <p className="font-serif text-2xl sm:text-3xl text-onyx mb-6">Prête pour votre premier rituel ?</p>
            <Link
              to="/produit"
              data-testid="faq-cta-button"
              className="inline-flex items-center gap-3 rounded-full bg-onyx text-cream px-8 py-4 text-sm font-semibold hover:bg-terracotta transition-colors"
            >
              Je découvre le masque <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Home;
